import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, X, DollarSign, Shield, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const ZoomInfoVsNoQ5 = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "ZoomInfo vs NoQ5 — AI CRM Agent Comparison 2026",
      "description": "Compare ZoomInfo and NoQ5 for CRM enrichment, data quality, and sales intelligence automation.",
      "url": "https://noq5.ai/zoominfo-vs-noq5",
      "publisher": { "@type": "Organization", "name": "NoQ5", "url": "https://noq5.ai" }
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="ZoomInfo vs NoQ5 — CRM Enrichment & AI Agent Comparison (2026)"
        description="ZoomInfo vs NoQ5: Compare data enrichment, CRM automation, and pricing. See why teams switch from expensive ZoomInfo contracts to NoQ5's AI CRM agent."
        keywords="ZoomInfo vs NoQ5, ZoomInfo alternative, ZoomInfo comparison, ZoomInfo CRM alternative, best ZoomInfo alternative, cheaper than ZoomInfo"
        canonicalUrl="https://noq5.ai/zoominfo-vs-noq5"
        jsonLd={jsonLd}
      />

      <section className="pt-24 pb-16 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6">COMPARISON</span>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">ZoomInfo vs NoQ5</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            ZoomInfo charges $15,000-$40,000/year for a contact database. NoQ5 gives you AI-powered enrichment plus full CRM automation at a fraction of the cost.
          </p>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Feature Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full bg-card rounded-xl border border-border">
              <thead>
                <tr className="border-b border-border">
                  <th className="p-4 text-left font-bold">Feature</th>
                  <th className="p-4 text-center font-bold">ZoomInfo</th>
                  <th className="p-4 text-center font-bold text-primary">NoQ5</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Contact database", "Proprietary DB", "50+ enrichment sources"],
                  ["Data enrichment", "Manual export/import", "Autonomous into CRM"],
                  ["CRM automation", "No", "Full autonomous agent"],
                  ["Pipeline management", "No", "AI-powered"],
                  ["Meeting intelligence", "No", "Auto-capture & update"],
                  ["Revenue forecasting", "No", "Evidence-based"],
                  ["Annual pricing", "$15K-$40K+", "Fraction of the cost"],
                  ["Contract lock-in", "Annual contract", "Monthly, cancel anytime"],
                  ["GDPR compliance", "Compliance issues reported", "EU-first, GDPR native"],
                  ["Setup time", "Weeks", "5 minutes"]
                ].map(([feature, zoom, noq5], i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="p-4 font-medium">{feature}</td>
                    <td className="p-4 text-center text-muted-foreground text-sm">{zoom}</td>
                    <td className="p-4 text-center text-primary font-medium text-sm">{noq5}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Key Differences */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Why Teams Switch from ZoomInfo</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: DollarSign, title: "Cost", desc: "ZoomInfo costs $15K-$40K+/year for data access alone. NoQ5 includes enrichment as part of full CRM automation — at a fraction of the price." },
              { icon: Zap, title: "Execution", desc: "ZoomInfo gives you data. You still have to manually enrich records, update CRM fields, and manage pipelines. NoQ5 does all of that autonomously." },
              { icon: Shield, title: "Compliance", desc: "ZoomInfo has faced GDPR scrutiny in Europe. NoQ5 is EU-first with GDPR compliance built into every workflow." }
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border">
                <item.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl font-bold mb-6">Stop Paying $40K for a Database</h2>
          <p className="text-xl opacity-90 mb-8">Get AI-powered enrichment plus full CRM automation. NoQ5 replaces ZoomInfo and your CRM admin work — at a fraction of the cost.</p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/trial">Start Free Trial <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default ZoomInfoVsNoQ5;
